/*
 * ScheduleCell
 *
 * Renders a single class block inside a day's column. The parent grid positions
 * this element using absolute positioning; this component computes the vertical
 * offset (top) and height of the block from the schedule metadata.
 *
 * Contract / inputs:
 * - entry: an object representing a class instance. Expected fields (produced
 *   by scheduleUtils.buildScheduleGrid):
 *     - className: string
 *     - start: 'HH:MM' (24-hour string)
 *     - end: 'HH:MM' (24-hour string)
 *     - topOffset: number (minutes offset from 12:00 PM used for positioning)
 *     - type: array of type codes (e.g. ['ST'])
 *     - level: array of level codes (e.g. ['BEG'])
 * - filters: { type: 'ST'|'GR'|..., level: 'ALL'|'BEG'|'ADV' }
 * - slotHeight: number of pixels that represent 60 minutes (one hour) in the UI
 * - isMobile / abbreviateName: presentation for mobile
 *
 * Notes / assumptions:
 * - start/end are 'HH:MM' strings; helpers from scheduleUtils convert them to
 *   durations and formatted labels. topOffset is already in minutes.
 * - This component does not mutate the entry object; it only reads values and
 *   computes display properties.
 */

import { Box, Typography, Tooltip } from '@mui/material'; 
import {
  formatTime12,
  getDurationInHours,
  getColorByType
} from './scheduleUtils';
import { formatTimeRangeCompact } from './scheduleUtils';

function ScheduleCell({ entry, filters, slotHeight, isMobile = false, abbreviateName = false }) {
  const {
    className,
    start,          
    end,
    type,         
    topOffset,   
    level         
  } = entry;


  const adjustedOffset = topOffset * (slotHeight / 60); // minutes -> px
  const height = getDurationInHours(start, end) * slotHeight; // hours -> px
  const color = getColorByType(type);

  // Overlap handling removed — backend prevents overlapping classes of the same type
  const overlapTotalAdjusted = 1;
  const overlapIndexAdjusted = 0;

  // When multiple entries overlap we reduce font sizes and spacing so content fits
  const shrinkForOverlap = overlapTotalAdjusted >= 2;
  const titleFontSize = shrinkForOverlap
    ? { xs: '0.72rem', sm: '0.9rem', md: '1rem' }
    : { xs: '0.85rem', sm: '1rem', md: '1.1rem' };

  
  // Slightly smaller time/level fonts for mobile/small/medium screens
  const timeFontSize = shrinkForOverlap
    ? { xs: '0.5rem', sm: '0.62rem', md: '0.72rem' }
    : { xs: '0.55rem', sm: '0.68rem', md: '0.78rem' };
  const levelFontSize = shrinkForOverlap
    ? { xs: '0.55rem', sm: '0.64rem', md: '0.74rem' }
    : { xs: '0.6rem', sm: '0.7rem', md: '0.8rem' };
  const lineGap = shrinkForOverlap ? 0.125 : 0.25;


  // Determine if this cell matches the current filter settings
  const filterMatch =
    type.includes(filters.type) &&
    (filters.level === 'ALL' || level.includes(filters.level));

  // If the cell does not match the filters, do not render it
  if (!filterMatch) return null;


  // Map level codes to readable labels
  const levelLabels = {
    BEG: 'Beginner',
    ADV: 'Advanced',
    FT: 'Fight Team'
  };

  // If both Beginner and Advanced are present we consider it 'All Levels' for
  // display purposes. finalReadableLevel is an array (join with commas when shown).
  const readableLevel = level.map(code => levelLabels[code] || code);
  const isAllLevels = level.map(code => levelLabels[code] || code).includes('Beginner') && readableLevel.includes('Advanced');
  const finalReadableLevel = isAllLevels ? ['All Levels'] : readableLevel;

  // Tooltip content showing full details (title, time range, levels)
  const tooltipContent = (
    <Box sx={{ px: 1, py: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1.05rem' } }}>
        {className}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, fontSize: { xs: '0.6rem', sm: '0.75rem', md: '0.85rem' } }}>
        {formatTimeRangeCompact(start, end)}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25, fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.9rem' } }}>
        {finalReadableLevel.join(', ')}
      </Typography>
    </Box>
  );

  // Compute displayTitle with abbreviation for mobile
  const computeDisplayTitle = (name) => {
    if (!abbreviateName || !name) return name;
    // Case-insensitive replacement for 'Muay Thai' -> 'MT'
    try {
      return name.replace(/Muay\s+Thai/ig, 'MT');
    } catch (e) {
      return name;
    }
  };

  const displayTitle = computeDisplayTitle(className);

  return (
    // Tooltip wraps the cell so hover/tap shows full details
    <Tooltip title={tooltipContent} arrow placement="top" enterDelay={200} leaveDelay={100}>
      <Box
        sx={{
          position: 'absolute',
          top: `${adjustedOffset}px`,
          left: `${(100 / overlapTotalAdjusted) * overlapIndexAdjusted}%`,
          width: `${100 / overlapTotalAdjusted}%`,
          height: `${height}px`,
          backgroundColor: color,
          borderRadius: 2,
          px: { xs: 0.5, sm: 1 },
          py: { xs: 0.5, sm: 1 },
          border: '1px solid #fff',
          overflow: 'hidden',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          transition: 'box-shadow 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 0 8px rgba(255,255,255,0.4)'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: lineGap, width: '100%', height: '100%' }}>
          {/* Top: title only */}
          <Box>
            <Typography
              variant={isMobile ? 'body2' : 'body1'}
              fontWeight="bold"
              sx={{
                lineHeight: isMobile ? 1.05 : 1.1,
                fontSize: isMobile ? { xs: '0.75rem' } : titleFontSize,
              }}
              title={className}
            >
              {displayTitle}
            </Typography>
          </Box>

          {/* Time: placed directly under the title */}
          <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: lineGap }}>
            {isMobile ? (
              <Typography
                variant="caption"
                sx={{ fontSize: { xs: '0.65rem' }, opacity: 0.95, whiteSpace: 'nowrap', lineHeight: 1 }}
                title={formatTime12(start)}
              >
                {formatTime12(start)}
              </Typography>
            ) : (
              <Typography
                variant="caption"
                  sx={{
                    opacity: 0.95,
                    fontSize: timeFontSize,
                    whiteSpace: 'nowrap',
                    lineHeight: 1
                  }}
                  title={`${formatTime12(start)} – ${formatTime12(end)}`}
              >
                {formatTimeRangeCompact(start, end)}
              </Typography>
            )}

            {/* Level: Under time */}
            <Typography
              variant="caption"
              sx={{
                opacity: 0.9,
                fontSize: levelFontSize,
              }}
              title={finalReadableLevel.join(', ')}
              style={{lineHeight: 1 }}
            >
              {finalReadableLevel.join(', ')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
}

export default ScheduleCell;
